import { updateDoc } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';

import { useOutletContext } from 'react-router-dom';

import { nameOf } from '../members';

function SubmissionDisplay({
    submission,
    setCurSubDetails,
}) {

    if (submission == null) {
        return <input tabIndex={-1} type='checkbox' checked={false} readOnly></input>
    }

    const [id, data] = submission;
    const noPhoto = !data.image;   // completed but no photo -> gray the checkbox

    return <input
        type='checkbox'
        tabIndex={-1}
        readOnly={true}
        checked={true}
        onClick={(e) => setCurSubDetails(data)}
        title={noPhoto ? "completed — no photo" : undefined}
        style={noPhoto ? { accentColor: "#999" } : undefined}
    />
}


function Note({
    item,
    curPlayer
}) {
    const [note, setNote] = useState(item.data()?.notes?.[curPlayer] ?? "");
    const initial = item.data()?.notes?.[curPlayer] ?? "";

    // Firebase is only updated 1000ms after user starts typing
    // Sets a timeout, and cleans it up if note is changed again
    useEffect(() => {
        if (note === initial) return;
        const timeoutRef = setTimeout(() => {
            updateDoc(item.ref, {
                ["notes." + curPlayer]: note
            })
        }, 1000)

        return () => clearTimeout(timeoutRef);
    }, [note]);

    return <input
        value={note}
        onChange={(e) => {setNote(e.target.value)}}
    />

}


function SummaryTable({
    items,
    subms,
    players,
    setCurSubDetails,
}) {
    const { uid: curPlayer } = useOutletContext();

    // Members come first, in whatever order `useHuntPlayers` returned them.
    const memberColumns = players.docs.map((p) => ({
        path: p.ref.path,
        id: p.id,
        label: nameOf(p),
    }));
    const memberPaths = new Set(memberColumns.map((c) => c.path));

    // Anyone who has submissions but isn't a current member (e.g. a legacy
    // name-keyed player that didn't resolve during migration) gets a trailing
    // column so their submissions stay visible instead of being silently
    // dropped at `submsTable[-1] = ...`. Label = the player doc id, which is
    // their old name for legacy data and a uid otherwise.
    const formerColumns = [];
    const seenFormer = new Set();
    subms.docs.forEach((s) => {
        const ref = s.data().player;
        if (!ref || memberPaths.has(ref.path) || seenFormer.has(ref.path)) return;
        seenFormer.add(ref.path);
        formerColumns.push({
            path: ref.path,
            id: ref.id,
            label: ref.id,
        });
    });

    const columns = [...memberColumns, ...formerColumns];
    const nameMap = new Map(columns.map((c) => [c.id, c.label]));

    const leaderboard = new Map(columns.map((c) => [c.path, 0]));
    const submsTable = new Map(items.docs.map((item) => [item.ref.path, columns.map(() => null)]));
    const columnIndex = new Map(columns.map((c, i) => [c.path, i]));

    subms.docs.forEach((s) => {
        const d = s.data();
        const itemPath = d.item?.path;
        const playerPath = d.player?.path;
        if (!itemPath || !playerPath) return;
        // If an item is missing from the items query (e.g. the orderBy('author')
        // filtered it out), skip rather than crash on a missing map entry.
        if (!submsTable.has(itemPath)) return;
        const idx = columnIndex.get(playerPath);
        if (idx === undefined) return; // shouldn't happen — formerColumns covers this
        leaderboard.set(playerPath, leaderboard.get(playerPath) + 1);
        submsTable.get(itemPath)[idx] = [s.id, d];
    });

    return (
    <table>
        <thead>
            <tr>
                <th>Item</th>
                {columns.map((c) => <th key={c.path}>{c.label}</th>)}
                <th>Notes</th>
            </tr>
        </thead>

        <tbody>
            <tr>
                <td><b>Leaderboard</b></td>
                {columns.map((c) => <td key={c.path}>{leaderboard.get(c.path) ?? 0}</td>)}
            </tr>

            {items.docs.map((item) => <tr key={item.id}>
                <td>{item.data().desc}</td>
                {submsTable.get(item.ref.path).map((entry, i) => (
                    <td key={item.id + "-" + i}>
                        <SubmissionDisplay
                            submission={entry}
                            setCurSubDetails={(s) => setCurSubDetails({
                                ...s,
                                itemDesc: item.data().desc,
                                playerName: nameMap.get(s.player.id) ?? s.player.id,
                            })}
                        />
                    </td>
                ))}
                <td>
                    <Note item={item} curPlayer={curPlayer}/>
                </td>
            </tr>)}
        </tbody>
    </table>
    );
}


export default SummaryTable;
